import { Lock, Unlock, Users, CheckSquare, Square, UserMinus, UserCheck } from "lucide-react";

export interface TableInfo {
  id: string;
  name: string;
  isClosed: boolean;
  userCount: number;
  capacity: number;
  restaurantName?: string;
  neighborhood?: string;
}

interface TableSelectorProps {
  tables: TableInfo[];
  selectedTableIds: string[];
  onSelectionChange: (tableIds: string[]) => void;
}

const UNASSIGNED_TABLE_ID = "unassigned-users";

export function TableSelector({
  tables,
  selectedTableIds,
  onSelectionChange,
}: TableSelectorProps) {
  // Sort tables: closed first, then by userCount descending
  const sortedTables = [...tables].sort((a, b) => {
    // First sort by locked status (closed/locked first)
    if (a.isClosed !== b.isClosed) {
      return a.isClosed ? -1 : 1;
    }
    // Then sort by userCount descending
    return b.userCount - a.userCount;
  });

  const closedTables = tables.filter((t) => t.isClosed);
  const openTables = tables.filter((t) => !t.isClosed);
  const tablesWithUsers = tables.filter((t) => t.userCount > 0);
  
  const unassignedTable = tables.find((t) => t.id === UNASSIGNED_TABLE_ID);
  const assignedTables = tables.filter(
    (t) => t.id !== UNASSIGNED_TABLE_ID && t.userCount > 0
  );

  const handleSelectAll = () => {
    onSelectionChange(tables.map((t) => t.id));
  };

  const handleSelectClosed = () => {
    onSelectionChange(closedTables.map((t) => t.id));
  };

  const handleSelectOpen = () => {
    onSelectionChange(openTables.map((t) => t.id));
  };

  const handleSelectWithUsers = () => {
    onSelectionChange(tablesWithUsers.map((t) => t.id));
  };

  const handleSelectAssigned = () => {
    onSelectionChange(assignedTables.map((t) => t.id));
  };

  const handleSelectUnassigned = () => {
    if (unassignedTable) {
      onSelectionChange([unassignedTable.id]);
    }
  };

  const handleClearSelection = () => {
    onSelectionChange([]);
  };

  const toggleTable = (tableId: string) => {
    if (selectedTableIds.includes(tableId)) {
      onSelectionChange(selectedTableIds.filter((id) => id !== tableId));
    } else {
      onSelectionChange([...selectedTableIds, tableId]);
    }
  };

  const isAllSelected = selectedTableIds.length === tables.length;

  return (
    <div className="space-y-2">
      {/* Quick Selection Buttons */}
      <div className="flex flex-wrap gap-1">
        <button
          onClick={isAllSelected ? handleClearSelection : handleSelectAll}
          className={`btn btn-xs ${
            isAllSelected ? "btn-primary" : "btn-ghost"
          }`}
        >
          {isAllSelected ? (
            <CheckSquare className="w-3 h-3 mr-0.5" />
          ) : (
            <Square className="w-3 h-3 mr-0.5" />
          )}
          <span className="text-xs">All ({tables.length})</span>
        </button>
        <button
          onClick={handleSelectAssigned}
          className="btn btn-xs btn-ghost"
          disabled={assignedTables.length === 0}
        >
          <UserCheck className="w-3 h-3 mr-0.5" />
          <span className="text-xs">Assigned ({assignedTables.length})</span>
        </button>
        {unassignedTable && (
          <button
            onClick={handleSelectUnassigned}
            className="btn btn-xs btn-ghost"
          >
            <UserMinus className="w-3 h-3 mr-0.5" />
            <span className="text-xs">Unassigned ({unassignedTable.userCount})</span>
          </button>
        )}
        <button
          onClick={handleSelectClosed}
          className="btn btn-xs btn-ghost"
          disabled={closedTables.length === 0}
        >
          <Lock className="w-3 h-3 mr-0.5" />
          <span className="text-xs">Closed ({closedTables.length})</span>
        </button>
        <button
          onClick={handleSelectOpen}
          className="btn btn-xs btn-ghost"
          disabled={openTables.length === 0}
        >
          <Unlock className="w-3 h-3 mr-0.5" />
          <span className="text-xs">Open ({openTables.length})</span>
        </button>
        <button
          onClick={handleSelectWithUsers}
          className="btn btn-xs btn-ghost"
          disabled={tablesWithUsers.length === 0}
        >
          <Users className="w-3 h-3 mr-0.5" />
          <span className="text-xs">With Users ({tablesWithUsers.length})</span>
        </button>
        {selectedTableIds.length > 0 && (
          <button
            onClick={handleClearSelection}
            className="btn btn-xs btn-ghost ml-auto"
          >
            <span className="text-xs">Clear</span>
          </button>
        )}
      </div>

      {/* Selected Count */}
      <div className="bg-base-200 rounded px-2 py-1">
        <p className="text-xs font-medium">
          {selectedTableIds.length} table(s) selected
        </p>
      </div>

      {/* Table List */}
      <div className="max-h-64 overflow-y-auto space-y-1 border border-base-300 rounded p-1">
        {sortedTables.length === 0 ? (
          <div className="text-center py-4 text-base-content/60 text-xs">
            No tables available
          </div>
        ) : (
          sortedTables.map((table) => {
            const isSelected = selectedTableIds.includes(table.id);
            const hasUsers = table.userCount > 0;

            return (
              <div
                key={table.id}
                onClick={() => toggleTable(table.id)}
                className={`cursor-pointer transition-all rounded p-2 ${
                  isSelected
                    ? "bg-primary/10 border border-primary"
                    : "hover:bg-base-200 border border-transparent"
                } ${!hasUsers ? "opacity-50" : ""}`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleTable(table.id)}
                    className="checkbox checkbox-xs"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-xs truncate">
                        {table.name}
                      </span>
                      {table.isClosed ? (
                        <Lock className="w-3 h-3 text-accent shrink-0" />
                      ) : (
                        <Unlock className="w-3 h-3 text-base-content/30 shrink-0" />
                      )}
                    </div>
                    {table.restaurantName && (
                      <p className="text-[10px] text-base-content/50 truncate leading-tight">
                        {table.restaurantName}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5 text-[10px] text-base-content/60 shrink-0">
                    <Users className="w-3 h-3" />
                    <span className={hasUsers ? "font-medium" : ""}>
                      {table.userCount}/{table.capacity}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

